import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Target, BarChart3, Zap, Users, Brain, MessageCircle, LayoutGrid, Presentation, CheckCircle2, Globe, Activity, Droplets, Heart, FlaskConical, Stethoscope, Printer, Database, MousePointerClick, TrendingUp, Eye } from 'lucide-react';
import { SPECIALTY_COLORS } from '../types';
interface CaseStudyProps {
  onBack: () => void;
}
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} data-id="element-24">
    {children}
  </motion.div>;
export function CaseStudy({
  onBack
}: CaseStudyProps) {
  return <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-brand selection:text-white print:bg-white" data-id="element-25">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 print:hidden" data-id="element-26">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-27">
          <button onClick={onBack} className="flex items-center text-sm font-semibold text-gray-500 hover:text-dark transition-colors" data-id="element-28">
            <ArrowLeft size={18} className="mr-2" data-id="element-29" />
            Back to Workshop
          </button>
          <div className="flex items-center gap-4" data-id="element-30">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400 hidden sm:inline" data-id="element-31">
              Project Case Study
            </span>
            <button onClick={() => window.print()} className="flex items-center text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-full transition-colors" data-id="element-32">
              <Printer size={16} className="mr-2" data-id="element-33" />
              Save PDF
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-32 pb-20 px-6 relative overflow-hidden print:pt-12 print:pb-10" data-id="element-34">
        <div className="absolute top-[-20%] right-[-10%] w-[70vw] h-[70vw] rounded-full bg-brand/5 blur-3xl -z-10 print:hidden" data-id="element-35" />
        <div className="max-w-4xl mx-auto text-center" data-id="element-36">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-37">
            <span className="inline-block py-1.5 px-4 rounded-full bg-gray-100 text-gray-600 text-sm font-bold tracking-widest uppercase mb-6" data-id="element-38">
              Client: Global Strategic Insights
            </span>
          </motion.div>
          <motion.h1 className="text-5xl md:text-7xl font-bold leading-tight mb-8 text-dark tracking-tight print:text-4xl" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-39">
            From Passive Readouts to{' '}
            <span className="text-brand" data-id="element-40">Active Discovery</span>
          </motion.h1>
          <motion.p className="text-xl md:text-2xl text-gray-500 leading-relaxed max-w-3xl mx-auto print:text-lg" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} data-id="element-41">
            How we transformed a 4-specialty CKD market research report into an
            interactive, competitive workshop that helps commercial teams
            sharpen their strategy.
          </motion.p>
        </div>
      </header>

      {/* The Challenge & Solution */}
      <section className="py-20 px-6 bg-gray-50 print:py-10 print:bg-white" data-id="element-42">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-43">
          <FadeIn data-id="element-44">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4" data-id="element-45">
              The Challenge
            </h2>
            <h3 className="text-3xl font-bold mb-6 text-dark" data-id="element-46">
              Death by PowerPoint
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-47">
              Global Strategic Insights conducted extensive market research
              among PCPs, Endocrinologists, Cardiologists, and Nephrologists
              regarding CKD management behaviors.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-48">
              But traditional slide decks weren't engaging enough. They needed a
              new way to present findings that would actively help commercial
              teams internalize the data and use it to sharpen segmentation,
              prioritize features, and execute on the right motivations.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-49">
            <h2 className="text-sm font-bold uppercase tracking-widest text-brand mb-4" data-id="element-50">
              The Solution
            </h2>
            <h3 className="text-3xl font-bold mb-6 text-dark" data-id="element-51">
              Specialty Gap Explorer
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-52">
              An interactive workshop tool designed for 4 groups of 10 people.
              Each table owns one specialty. Instead of passively reading
              slides, teams predict, discuss, and discover insights through
              competitive exercises.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8" data-id="element-53">
              {[{
              icon: Droplets,
              color: SPECIALTY_COLORS.Nephrologist,
              label: 'Nephrologist'
            }, {
              icon: Heart,
              color: SPECIALTY_COLORS.Cardiologist,
              label: 'Cardiologist'
            }, {
              icon: FlaskConical,
              color: SPECIALTY_COLORS.Endocrinologist,
              label: 'Endocrinologist'
            }, {
              icon: Stethoscope,
              color: SPECIALTY_COLORS.PCP,
              label: 'PCP'
            }].map(spec => <div key={spec.label} className="flex items-center p-3 bg-white rounded-xl border border-gray-100 shadow-sm print:border-gray-200" data-id="element-54">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{
                backgroundColor: `${spec.color}15`,
                color: spec.color
              }} data-id="element-55">
                    <spec.icon size={16} data-id="element-56" />
                  </div>
                  <span className="font-semibold text-sm" data-id="element-57">{spec.label}</span>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Strategic Objectives */}
      <section className="py-24 px-6 print:py-12" data-id="element-58">
        <div className="max-w-6xl mx-auto text-center" data-id="element-59">
          <FadeIn data-id="element-60">
            <h2 className="text-3xl md:text-5xl font-bold mb-16 text-dark" data-id="element-61">
              Three Strategic Objectives
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-62">
            {[{
            icon: Target,
            title: 'Sharpen Segmentation',
            desc: 'Identify which patient profiles and physician mindsets represent the biggest opportunity.'
          }, {
            icon: BarChart3,
            title: 'Prioritize Arguments',
            desc: 'Match the right clinical data and product features to the specific barriers of each specialty.'
          }, {
            icon: Zap,
            title: 'Execute Motivation',
            desc: 'Find the emotional and rational framing required to move physicians from passive to active management.'
          }].map((obj, i) => <FadeIn key={obj.title} delay={i * 0.2} data-id="element-63">
                <div className="p-8 rounded-3xl bg-white border border-gray-100 shadow-xl shadow-gray-200/40 h-full print:shadow-none print:border-gray-300" data-id="element-64">
                  <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center mx-auto mb-6" data-id="element-65">
                    <obj.icon size={28} data-id="element-66" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-dark" data-id="element-67">
                    {obj.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed" data-id="element-68">{obj.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* The Flow */}
      <section className="py-24 px-6 bg-dark text-white print:bg-white print:text-dark print:py-12" data-id="element-69">
        <div className="max-w-6xl mx-auto" data-id="element-70">
          <FadeIn data-id="element-71">
            <div className="text-center mb-16" data-id="element-72">
              <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-73">
                The 13-Step Workshop Flow
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto print:text-gray-500" data-id="element-74">
                A carefully choreographed journey from icebreaker to actionable
                strategy.
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-75">
            {[{
            phase: 'Icebreaker',
            color: 'bg-indigo-500',
            text: 'text-indigo-500',
            steps: ['Welcome', 'Group Quiz']
          }, {
            phase: 'Deep Dive',
            color: 'bg-cyan-500',
            text: 'text-cyan-500',
            steps: ['Group Setup', 'Specialty Mindset', 'Treatment Behavior', 'Patient Staging']
          }, {
            phase: 'Strategy',
            color: 'bg-amber-500',
            text: 'text-amber-500',
            steps: ['Strategy Menu', 'Segmentation', 'Arguments & Data', 'Motivations']
          }, {
            phase: 'Share Back',
            color: 'bg-emerald-500',
            text: 'text-emerald-500',
            steps: ['Build Card', 'Present', 'All Together']
          }].map((phase, i) => <FadeIn key={phase.phase} delay={i * 0.15} data-id="element-76">
                <div className="bg-white/10 rounded-3xl p-6 border border-white/10 h-full print:bg-gray-50 print:border-gray-200" data-id="element-77">
                  <div className="flex items-center mb-6" data-id="element-78">
                    <div className={`w-3 h-3 rounded-full ${phase.color} mr-3 print:hidden`} data-id="element-79" />
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${phase.text}`} data-id="element-80">
                      {phase.phase}
                    </h3>
                  </div>
                  <ul className="space-y-4" data-id="element-81">
                    {phase.steps.map((step, j) => <li key={step} className="flex items-start" data-id="element-82">
                        <span className="text-gray-500 mr-3 font-mono text-sm mt-0.5" data-id="element-83">
                          {i === 0 ? j + 1 : i === 1 ? j + 3 : i === 2 ? j + 7 : j + 11}
                          .
                        </span>
                        <span className="font-medium text-gray-200 print:text-gray-700" data-id="element-84">
                          {step}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 px-6 print:py-12" data-id="element-85">
        <div className="max-w-6xl mx-auto" data-id="element-86">
          <FadeIn data-id="element-87">
            <h2 className="text-3xl md:text-5xl font-bold mb-16 text-center text-dark" data-id="element-88">
              Key Features
            </h2>
          </FadeIn>

          <div className="space-y-24 print:space-y-12" data-id="element-89">
            {/* Feature 1 */}
            <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-90">
              <FadeIn className="flex-1" data-id="element-91">
                <div className="bg-blue-50 rounded-3xl p-10 border border-blue-100 print:border-gray-200" data-id="element-92">
                  <Brain className="text-blue-500 mb-6" size={40} data-id="element-93" />
                  <h3 className="text-2xl font-bold text-dark mb-4" data-id="element-94">
                    Predict-then-Reveal Mechanics
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed" data-id="element-95">
                    Instead of telling teams what the research says, the tool
                    asks them to predict it first. This surfaces internal
                    assumptions and creates a powerful "aha!" moment when the
                    actual data is revealed.
                  </p>
                </div>
              </FadeIn>
              <FadeIn className="flex-1" delay={0.2} data-id="element-96">
                <div className="bg-amber-50 rounded-3xl p-10 border border-amber-100 print:border-gray-200" data-id="element-97">
                  <MessageCircle className="text-amber-500 mb-6" size={40} data-id="element-98" />
                  <h3 className="text-2xl font-bold text-dark mb-4" data-id="element-99">
                    Built-in Discussion Prompts
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed" data-id="element-100">
                    Every data reveal is paired with targeted discussion
                    questions. The tool acts as a co-facilitator at every table,
                    ensuring conversations stay focused on actionable commercial
                    strategy rather than just admiring the problem.
                  </p>
                </div>
              </FadeIn>
            </div>

            {/* Feature 2 */}
            <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-101">
              <FadeIn className="flex-1" data-id="element-102">
                <div className="bg-emerald-50 rounded-3xl p-10 border border-emerald-100 print:border-gray-200" data-id="element-103">
                  <Activity className="text-emerald-500 mb-6" size={40} data-id="element-104" />
                  <h3 className="text-2xl font-bold text-dark mb-4" data-id="element-105">
                    Competitive Audio Feedback
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed" data-id="element-106">
                    Using the Web Audio API, the app plays distinct celebration
                    or error sounds when groups submit answers. Loud enough for
                    other tables to hear, it creates a lively, competitive
                    workshop atmosphere.
                  </p>
                </div>
              </FadeIn>
              <FadeIn className="flex-1" delay={0.2} data-id="element-107">
                <div className="bg-purple-50 rounded-3xl p-10 border border-purple-100 print:border-gray-200" data-id="element-108">
                  <LayoutGrid className="text-purple-500 mb-6" size={40} data-id="element-109" />
                  <h3 className="text-2xl font-bold text-dark mb-4" data-id="element-110">
                    Non-linear Strategy Hub
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed" data-id="element-111">
                    A central strategy menu allows groups to tackle
                    segmentation, arguments, and motivations in whatever order
                    makes sense for their discussion flow, breaking the rigid
                    linearity of standard presentations.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Gallery */}
      <section className="py-24 px-6 bg-gray-50 print:py-12 print:bg-white" data-id="element-112">
        <div className="max-w-6xl mx-auto" data-id="element-113">
          <FadeIn data-id="element-114">
            <div className="text-center mb-16" data-id="element-115">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-dark" data-id="element-116">
                The Experience in Action
              </h2>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-117">
                Interactive screens that turn passive audiences into active
                strategists.
              </p>
            </div>
          </FadeIn>

          {/* Screenshot Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-118">
            {/* Screenshot 1: Predict-then-Reveal */}
            <FadeIn delay={0.1} data-id="element-119">
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-white print:shadow-none" data-id="element-120">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white" data-id="element-121">
                  <div className="flex items-center mb-3" data-id="element-122">
                    <Brain size={20} className="mr-2 opacity-80" data-id="element-123" />
                    <span className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-124">
                      Deep Dive
                    </span>
                  </div>
                  <h4 className="text-xl font-bold" data-id="element-125">
                    Inside the Nephrologist Mindset
                  </h4>
                </div>
                <div className="p-6" data-id="element-126">
                  <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-127">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-128">
                      <span className="text-xs font-bold text-gray-400 uppercase" data-id="element-129">
                        What they believe
                      </span>
                      <div className="mt-2 space-y-2" data-id="element-130">
                        {['Late-stage specialist', 'Fragile patients', 'Evidence-motivated', 'Frustrated by late referrals'].map((b, i) => <div key={i} className="flex items-center text-xs text-gray-600" data-id="element-131">
                            <div className="w-1.5 h-1.5 rounded-full mr-2" style={{
                          backgroundColor: SPECIALTY_COLORS.Nephrologist
                        }} data-id="element-132" />
                            {b}
                          </div>)}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-center" data-id="element-133">
                      <MessageCircle size={24} className="text-blue-400 mb-2" data-id="element-134" />
                      <span className="text-xs font-bold text-gray-500" data-id="element-135">
                        Group predicts priorities
                      </span>
                      <span className="text-[10px] text-gray-400 mt-1" data-id="element-136">
                        Then reveal the data
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 italic text-center" data-id="element-137">
                    Groups discuss before the reveal — surfacing assumptions
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Screenshot 2: Ranking Exercise */}
            <FadeIn delay={0.2} data-id="element-138">
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-white print:shadow-none" data-id="element-139">
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 text-white" data-id="element-140">
                  <div className="flex items-center mb-3" data-id="element-141">
                    <TrendingUp size={20} className="mr-2 opacity-80" data-id="element-142" />
                    <span className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-143">
                      Strategy
                    </span>
                  </div>
                  <h4 className="text-xl font-bold" data-id="element-144">
                    Rank Their Drivers & Barriers
                  </h4>
                </div>
                <div className="p-6" data-id="element-145">
                  <div className="space-y-2 mb-4" data-id="element-146">
                    {[{
                    label: 'Safety data',
                    score: 90,
                    color: SPECIALTY_COLORS.Nephrologist
                  }, {
                    label: 'Guidelines',
                    score: 70,
                    color: `${SPECIALTY_COLORS.Nephrologist}AA`
                  }, {
                    label: 'Outcome evidence',
                    score: 60,
                    color: `${SPECIALTY_COLORS.Nephrologist}77`
                  }, {
                    label: 'Peer behavior',
                    score: 40,
                    color: `${SPECIALTY_COLORS.Nephrologist}44`
                  }].map((item, i) => <div key={i} data-id="element-147">
                        <div className="flex items-center justify-between mb-1" data-id="element-148">
                          <div className="flex items-center" data-id="element-149">
                            <span className="w-5 h-5 rounded text-white text-[10px] font-bold flex items-center justify-center mr-2" style={{
                          backgroundColor: SPECIALTY_COLORS.Nephrologist
                        }} data-id="element-150">
                              {i + 1}
                            </span>
                            <span className="text-xs font-semibold text-gray-700" data-id="element-151">
                              {item.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-152">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${i === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-153">
                              {i === 0 ? '✓ Correct' : `You said #${i + 2}`}
                            </span>
                            <span className="text-xs font-bold" style={{
                          color: SPECIALTY_COLORS.Nephrologist
                        }} data-id="element-154">
                              {item.score}%
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-155">
                          <motion.div className="h-full rounded-full" style={{
                        backgroundColor: item.color,
                        width: `${item.score}%`
                      }} initial={{
                        width: 0
                      }} whileInView={{
                        width: `${item.score}%`
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1,
                        delay: 0.3 + i * 0.15
                      }} data-id="element-156" />
                        </div>
                      </div>)}
                  </div>
                  <div className="text-xs text-gray-400 italic text-center" data-id="element-157">
                    Animated bar chart reveals after group ranking
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Screenshot 3: Strategy Menu */}
            <FadeIn delay={0.3} data-id="element-158">
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-white print:shadow-none" data-id="element-159">
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6 text-white" data-id="element-160">
                  <div className="flex items-center mb-3" data-id="element-161">
                    <LayoutGrid size={20} className="mr-2 opacity-80" data-id="element-162" />
                    <span className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-163">
                      Choose Your Path
                    </span>
                  </div>
                  <h4 className="text-xl font-bold" data-id="element-164">Strategy Exercises</h4>
                </div>
                <div className="p-6" data-id="element-165">
                  <div className="space-y-3" data-id="element-166">
                    {[{
                    icon: Target,
                    title: 'Segmentation & Targeting',
                    color: 'bg-blue-500',
                    done: true
                  }, {
                    icon: BarChart3,
                    title: 'Arguments & Data',
                    color: 'bg-amber-500',
                    done: false
                  }, {
                    icon: Zap,
                    title: 'Motivations',
                    color: 'bg-emerald-500',
                    done: false
                  }].map((ch, i) => <div key={i} className={`flex items-center p-3 rounded-xl border ${ch.done ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-id="element-167">
                        <div className={`w-8 h-8 rounded-lg ${ch.color} text-white flex items-center justify-center mr-3`} data-id="element-168">
                          {ch.done ? <CheckCircle2 size={16} data-id="element-169" /> : <ch.icon size={16} data-id="element-170" />}
                        </div>
                        <span className={`text-sm font-semibold ${ch.done ? 'text-gray-400' : 'text-dark'}`} data-id="element-171">
                          {ch.title}
                        </span>
                        {ch.done && <span className="ml-auto text-[10px] font-bold text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-172">
                            Done
                          </span>}
                      </div>)}
                  </div>
                  <div className="text-xs text-gray-400 italic text-center mt-4" data-id="element-173">
                    Groups choose their own strategy path
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Screenshot 4: Presentation Card */}
            <FadeIn delay={0.4} data-id="element-174">
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg bg-white print:shadow-none" data-id="element-175">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white" data-id="element-176">
                  <div className="flex items-center mb-3" data-id="element-177">
                    <Presentation size={20} className="mr-2 opacity-80" data-id="element-178" />
                    <span className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-179">
                      Share Back
                    </span>
                  </div>
                  <h4 className="text-xl font-bold" data-id="element-180">Group Strategy Card</h4>
                </div>
                <div className="p-6" data-id="element-181">
                  <div className="aspect-video bg-dark rounded-xl p-5 flex flex-col justify-between text-white relative overflow-hidden" data-id="element-182">
                    <div className="absolute top-0 right-0 w-24 h-full" style={{
                    backgroundColor: SPECIALTY_COLORS.Cardiologist,
                    opacity: 0.15
                  }} data-id="element-183" />
                    <div data-id="element-184">
                      <div className="flex items-center mb-2" data-id="element-185">
                        <Heart size={14} className="mr-1.5" style={{
                        color: SPECIALTY_COLORS.Cardiologist
                      }} data-id="element-186" />
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{
                        color: SPECIALTY_COLORS.Cardiologist
                      }} data-id="element-187">
                          Team Alpha — Cardiologist
                        </span>
                      </div>
                      <div className="space-y-2 mt-3" data-id="element-188">
                        <div data-id="element-189">
                          <span className="text-[9px] uppercase tracking-wider text-gray-400" data-id="element-190">
                            Segmentation
                          </span>
                          <p className="text-[11px] text-gray-300" data-id="element-191">
                            Outcome-driven cardiologists with dual CKD/CV
                            patients
                          </p>
                        </div>
                        <div data-id="element-192">
                          <span className="text-[9px] uppercase tracking-wider text-gray-400" data-id="element-193">
                            Key Arguments
                          </span>
                          <p className="text-[11px] text-gray-300" data-id="element-194">
                            CV outcome data + dual organ protection evidence
                          </p>
                        </div>
                        <div data-id="element-195">
                          <span className="text-[9px] uppercase tracking-wider text-gray-400" data-id="element-196">
                            Motivation
                          </span>
                          <p className="text-[11px] text-gray-300" data-id="element-197">
                            "One decision protects both organs"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 italic text-center mt-4" data-id="element-198">
                    16:9 projector-ready cards for cross-group share-back
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Engagement callout */}
          <FadeIn delay={0.3} data-id="element-199">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col md:flex-row items-center gap-8 max-w-4xl mx-auto shadow-sm print:shadow-none" data-id="element-200">
              <div className="flex gap-4 flex-shrink-0" data-id="element-201">
                <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center" data-id="element-202">
                  <Activity size={28} data-id="element-203" />
                </div>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center" data-id="element-204">
                  <MousePointerClick size={28} data-id="element-205" />
                </div>
              </div>
              <div data-id="element-206">
                <h4 className="text-xl font-bold text-dark mb-2" data-id="element-207">
                  Every click, every prediction, every debate
                </h4>
                <p className="text-gray-500 leading-relaxed" data-id="element-208">
                  Celebration sounds ring out when a group gets it right — loud
                  enough for other tables to hear. Wrong answers trigger a
                  different tone. The result: a lively, competitive atmosphere
                  where 40 people are actively engaged with research data
                  instead of checking their phones.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Impact */}
      <section className="py-24 px-6 bg-gray-50 print:py-12 print:bg-white" data-id="element-209">
        <div className="max-w-4xl mx-auto text-center" data-id="element-210">
          <FadeIn data-id="element-211">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-8" data-id="element-212">
              <CheckCircle2 size={32} data-id="element-213" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-dark" data-id="element-214">
              The Impact
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-12" data-id="element-215">
              By transforming a passive research readout into an active learning
              experience, teams retain more information because they discover
              the insights themselves.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left" data-id="element-216">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 print:border-gray-300" data-id="element-217">
                <h4 className="font-bold text-dark mb-2" data-id="element-218">Active Learning</h4>
                <p className="text-sm text-gray-500" data-id="element-219">
                  Groups internalize data better through prediction and debate.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 print:border-gray-300" data-id="element-220">
                <h4 className="font-bold text-dark mb-2" data-id="element-221">System View</h4>
                <p className="text-sm text-gray-500" data-id="element-222">
                  Cross-specialty share-backs reveal where patients fall through
                  the cracks.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 print:border-gray-300" data-id="element-223">
                <h4 className="font-bold text-dark mb-2" data-id="element-224">Reusable Format</h4>
                <p className="text-sm text-gray-500" data-id="element-225">
                  A scalable workshop framework for future market research
                  rollouts.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Snakker Data Capture */}
      <section className="py-24 px-6 print:py-12" data-id="element-226">
        <div className="max-w-5xl mx-auto" data-id="element-227">
          <FadeIn data-id="element-228">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 md:p-14 text-white relative overflow-hidden print:bg-gray-100 print:text-dark" data-id="element-229">
              {/* Background pattern */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 print:hidden" data-id="element-230" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 print:hidden" data-id="element-231" />

              <div className="relative z-10" data-id="element-232">
                <div className="flex items-center mb-6" data-id="element-233">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mr-4 print:bg-gray-200" data-id="element-234">
                    <Database size={24} className="text-brand" data-id="element-235" />
                  </div>
                  <div data-id="element-236">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-brand mb-1" data-id="element-237">
                      Powered by Snakker
                    </h3>
                    <h2 className="text-3xl md:text-4xl font-bold" data-id="element-238">
                      Every Interaction Captured
                    </h2>
                  </div>
                </div>

                <p className="text-lg text-gray-300 leading-relaxed mb-10 max-w-3xl print:text-gray-600" data-id="element-239">
                  Behind the scenes, Snakker captures every prediction, ranking,
                  selection, and group decision made during the workshop. This
                  interaction data creates a rich behavioral dataset that goes
                  far beyond what traditional surveys can capture.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-240">
                  {[{
                  icon: Eye,
                  title: 'Assumption Mapping',
                  desc: "See where teams' predictions diverge from reality — revealing blind spots in commercial understanding."
                }, {
                  icon: TrendingUp,
                  title: 'Continuous Learning',
                  desc: 'Each workshop run generates new data points, allowing the tool to surface patterns across sessions and teams.'
                }, {
                  icon: Target,
                  title: 'Strategy Refinement',
                  desc: 'Compare group strategies across workshops to identify which segmentation and messaging approaches gain the most traction.'
                }].map((item, i) => <div key={i} className="bg-white/5 rounded-2xl p-6 border border-white/10 print:bg-white print:border-gray-200" data-id="element-241">
                      <item.icon size={24} className="text-brand mb-4" data-id="element-242" />
                      <h4 className="font-bold text-white mb-2 print:text-dark" data-id="element-243">
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-400 leading-relaxed print:text-gray-500" data-id="element-244">
                        {item.desc}
                      </p>
                    </div>)}
                </div>

                <div className="bg-white/5 rounded-2xl p-6 border border-white/10 print:bg-white print:border-gray-200" data-id="element-245">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4" data-id="element-246">
                    <div className="flex items-center gap-3" data-id="element-247">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" data-id="element-248" />
                      <span className="text-sm font-semibold text-green-400 print:text-green-600" data-id="element-249">
                        Live data capture
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 print:text-gray-500" data-id="element-250">
                      Every workshop becomes smarter than the last. Snakker's
                      interaction layer means the tool doesn't just present
                      research — it{' '}
                      <strong className="text-white print:text-dark" data-id="element-251">
                        learns from how teams engage with it
                      </strong>
                      , enabling continuous improvement of both the content and
                      the commercial strategy it supports.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 text-center border-t border-gray-100 print:hidden" data-id="element-252">
        <button onClick={onBack} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-253">
          <ArrowLeft className="mr-2 w-5 h-5" data-id="element-254" />
          Return to Workshop
        </button>
      </footer>
    </div>;
}